/**
 * @fileoverview AutosaveStatus indicator
 *
 * Purpose: Tiny status pill rendered next to the editor's Close button to
 * communicate autosave state. Mirrors iconography used elsewhere in the
 * app (Loader2 spinner, Check, AlertCircle).
 */

import { useEffect, useState } from "react";
import { Check, Loader2, AlertCircle } from "lucide-react";
import { cn } from "@/utils/misc";
import type { AutosaveStatus as Status } from "./use-autosave";

interface AutosaveStatusProps {
  status: Status;
  lastSavedAt?: number | null;
  onRetry?: () => void;
  className?: string;
}

function formatRelative(now: number, then: number): string {
  const diffSec = Math.max(0, Math.floor((now - then) / 1000));
  if (diffSec < 5) return "just now";
  if (diffSec < 60) return `${diffSec}s ago`;
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
}

export function AutosaveStatus({
  status,
  lastSavedAt,
  onRetry,
  className,
}: AutosaveStatusProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (status !== "saved" || !lastSavedAt) return;
    const id = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, [status, lastSavedAt]);

  if (status === "saving") {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 text-xs text-primary/45",
          className,
        )}
      >
        <Loader2 className="size-3 animate-spin" />
        Saving…
      </span>
    );
  }

  if (status === "error") {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 text-xs text-destructive",
          className,
        )}
      >
        <AlertCircle className="size-3" />
        Save failed
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="ml-1 underline underline-offset-2 hover:text-destructive/80"
          >
            Retry
          </button>
        )}
      </span>
    );
  }

  if (status === "saved" && lastSavedAt) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400",
          className,
        )}
      >
        <Check className="size-3" />
        Saved {formatRelative(now, lastSavedAt)}
      </span>
    );
  }

  return null;
}
